import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  Select,
} from "@mui/material";
import {
  Box,
  InputAdornment,
  Tooltip,
  Radio,
  MenuItem,
  TextField,
  Button,
  Typography,
  IconButton,
  Grid,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import dropdown_black from "../../../../../../assets/AccountingModule/drop_down_icon.svg";
import humbbleIcon from "../../../../../../assets/AccountingModule/humbleicons_upload.svg";
import styles from "./style";
import { paymentTermsOptions } from "../../../../../../constants/data";
import color from "../../../../../../constants/color";
import { CreateSalesOrderValidation } from "../../../../../../helper/Validation";

const CreateSalesOrderPage = ({
  customerList,
  itemList,
  loading,
  createSalesOrder,
}) => {
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([
    {
      name: "",
      itemId: "",
      description: "",
      invoiceQuantity: 0,
      rate: 0,
      amount: 0,
    },
  ]);

  const [customerId, setCustomerId] = useState("");
  const [salesOrderNumber, setSalesOrderNumber] = useState("");
  const [salesOrderDate, setSalesOrderDate] = useState(null);
  const [expectedShipmentDate, setExpectedShipmentDate] = useState(null);
  const [paymentTerm, setPaymentTerm] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [subject, setSubject] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("N/A");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [filePreview, setFilePreview] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({
    customer: "",
    salesOrderNumber: "",
    salesOrderDate: "",
    expectedShipmentDate: "",
    paymentTerm: "",
    deliveryMethod: "",
    subject: "",
    items: [
      {
        item: "",
        description: "",
        quantity: "",
        rate: "",
      },
    ],
    customerNotes: "",
    termsAndConditions: "",
    discount: "",
    tax: "",
  });

  const navigate = useNavigate();

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: "",
        itemId: "",
        description: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ]);

    setErrors((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item: "",
          description: "",
          quantity: "",
          rate: "",
        },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = [...items];
      updatedItems.splice(index, 1);
      setItems(updatedItems);

      const updatedErrors = { ...errors };
      updatedErrors.items.splice(index, 1);
      setErrors(updatedErrors);
    }
  };

  const validateField = (fieldName, value) => {
    const rule = CreateSalesOrderValidation[fieldName];
    if (!rule) return "";

    // Required check
    if (rule.required && !value) {
      return rule.error || "This field is required";
    }

    // Pattern check
    if (rule.pattern && value && !rule.pattern.test(value)) {
      return rule.error;
    }

    // Custom validate function
    if (rule.validate && !rule.validate(value, { salesOrderDate })) {
      return rule.error;
    }

    // Length checks
    if (rule.maxLength && value.length > rule.maxLength) {
      return rule.error;
    }

    return "";
  };

  const isItemValid = items.some(
    (item) => item.itemId && item.name && item.invoiceQuantity > 0
  );

  const handleFormChange = (field, value) => {
    const error = validateField(field, value);

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    // Update the corresponding state
    switch (field) {
      case "salesOrderNumber":
        setSalesOrderNumber(value);
        break;
      case "salesOrderDate":
        setSalesOrderDate(value);
        break;
      case "expectedShipmentDate":
        setExpectedShipmentDate(value);
        break;
      case "paymentTerm":
        setPaymentTerm(value);
        break;
      case "deliveryMethod":
        setDeliveryMethod(value);
        break;
      case "subject":
        setSubject(value);
        break;
      case "customerNotes":
        setCustomerNotes(value);
        break;
      case "termsAndConditions":
        setTermsAndConditions(value);
        break;
      case "discount":
        setDiscountPercentage(Number(value) || 0);
        break;
      case "tax":
        setTaxPercentage(Number(value) || 0);
        break;
      default:
        break;
    }
  };

  const handleItemChange = (value, index, key) => {
    const updatedItems = [...items];
    const updatedErrors = { ...errors };

    if ((key === "quantity" || key === "rate") && Number(value) < 0) {
      return;
    }

    if (key === "quantity" || key === "rate") {
      updatedItems[index][key] = Number(value);
    } else {
      updatedItems[index][key] = value;
    }

    // Validate the changed field
    if (key === "name") {
      const error = validateField("item", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        item: error,
      };
    } else if (key === "description") {
      const error = validateField("description", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        description: error,
      };
    } else if (key === "quantity") {
      const error = validateField("quantity", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        quantity: error,
      };
    } else if (key === "rate") {
      const error = validateField("rate", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        rate: error,
      };
    }

    // Recalculate amount if quantity or rate changes
    if (key === "quantity" || key === "rate") {
      const qty = Number(updatedItems[index].quantity) || 0;
      const rate = Number(updatedItems[index].rate) || 0;
      updatedItems[index].amount = qty * rate;
    }

    if (key === "name") {
      const selectedItem = itemList?.find((item) => item.title === value);
      if (selectedItem) {
        updatedItems[index].itemId = selectedItem._id || "";
        updatedItems[index].rate = Number(selectedItem.sellingPrice || 0);
        updatedItems[index].quantity = Number(selectedItem.quantity || 0);
        updatedItems[index].invoiceQuantity = 0;
        updatedItems[index].description = selectedItem.description || "";
        updatedItems[index].amount = selectedItem.sellingPrice * 0;

        // Validate the auto-filled fields
        const quantityError = validateField(
          "quantity",
          updatedItems[index].quantity.toString()
        );
        const rateError = validateField(
          "rate",
          updatedItems[index].rate.toString()
        );

        updatedErrors.items[index] = {
          ...updatedErrors.items[index],
          quantity: quantityError,
          rate: rateError,
        };
      }
    }

    if (key === "quantity" || key === "rate") {
      updatedItems[index][key] = Number(value);
    } else {
      updatedItems[index][key] = value;
    }
    setItems(updatedItems);
    setErrors(updatedErrors);
  };

  const calculateSubTotal = () => {
    const total = items.reduce((sum, item) => {
      const amount =
        (Number(item?.invoiceQuantity) || 0) * (Number(item?.rate) || 0);
      return sum + amount;
    }, 0);
    return Number(total.toFixed(2)) || 0;
  };

  const calculateDiscountValue = () => {
    const subTotal = calculateSubTotal();
    const discount = (subTotal * (Number(discountPercentage) || 0)) / 100;
    return Number(discount.toFixed(2)) || 0;
  };

  const calculateTaxValue = () => {
    const subTotal = calculateSubTotal();
    const discountedAmount = subTotal - calculateDiscountValue();
    const tax = (discountedAmount * (Number(taxPercentage) || 0)) / 100;
    return Number(tax.toFixed(2)) || 0;
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const total = subTotal - discountValue + taxValue;
    return Number(total.toFixed(2)) || 0;
  };

  const CustomDropdownIcon = () => (
    <img
      src={dropdown_black}
      alt="dropdown"
      height={22}
      width={22}
      style={{ paddingRight: "10px" }}
    />
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const grandTotal = calculateGrandTotal();

    const payload = {
      customer: customerId,
      salesOrderNumber: salesOrderNumber,
      // reference: reference,
      salesOrderDate: salesOrderDate ? dayjs(salesOrderDate).format("YYYY-MM-DD") : null,
      expectedShipmentDate: expectedShipmentDate ? dayjs(expectedShipmentDate).format("YYYY-MM-DD") : null,
      paymentTerm: paymentTerm,
      deliveryMethod: deliveryMethod,
      // salesperson: salesperson,
      subject: subject,
      customerNotes: customerNotes,
      termsAndConditions: termsAndConditions,
      itemInformation: items.map((item) => ({
        itemId: item.itemId,
        name: item.name,
        description: item.description,
        rate: item.rate,
        quantity: item.invoiceQuantity,
        amount: (item.rate || 0) * (item.invoiceQuantity || 0),
      })),
      total: subTotal,
      discountPercentage: discountPercentage,
      discountValue: discountValue,
      taxPercentage: taxPercentage,
      taxValue: taxValue,
      grandTotal: grandTotal,
    };
    createSalesOrder(payload);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result.split(",")[1]; // Remove the data URL prefix
        setFilePreview(e.target.result);
        setAttachmentUrl(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview("");
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "&.Mui-error fieldset": {
        borderColor: "red",
        borderWidth: "2px",
      },
    },
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Box px="20px" mt={2}>
          <Box sx={styles.header}>
            <Typography sx={styles.title}>Add Sales Order</Typography>
          </Box>
        </Box>
        <Box sx={styles.container}>
          {/* Form Section */}
          <Box sx={styles.formSection}>
            <Grid container spacing={2}>
              {/* First Row */}
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Customer Name</Typography>
                  <Autocomplete
                    id="customer-select"
                    options={[
                      { _id: "add_new", name: "+ New Customer" },
                      ...(customerList || []),
                    ]}
                    getOptionLabel={(option) => option?.name || ""}
                    value={customer}
                    // popupIcon={<CustomDropdownIcon />}
                    onChange={(_, newValue) => {
                      if (newValue?._id === "add_new") {
                        navigate("/dashboard/addNewCustomer"); // redirect page
                        return;
                      }
                      setCustomer(newValue);
                      setCustomerId(newValue?._id || "");
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={
                          option._id === "add_new"
                            ? { color: color.secondary }
                            : {}
                        }
                      >
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Customer"
                        size="small"
                        sx={{ ...styles.textField, ...textFieldStyles }}
                        error={!!errors.customer}
                        helperText={errors.customer}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Sales Order#</Typography>
                  <TextField
                    fullWidth
                    placeholder="Sales Order"
                    size="small"
                    sx={{ ...styles.textField, ...textFieldStyles }}
                    onChange={(e) =>
                      handleFormChange("salesOrderNumber", e.target.value)
                    }
                    value={salesOrderNumber}
                    error={!!errors.salesOrderNumber}
                    helperText={errors.salesOrderNumber}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Sales Order Date</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      sx={{
                        ...styles.datePickerOuter,
                        borderRadius: "8px !important",
                      }}
                    >
                      <DatePicker
                        value={salesOrderDate}
                        onChange={(newValue) =>
                          handleFormChange("salesOrderDate", newValue)
                        }
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            sx={{
                              ...styles.datePicker,
                              ...textFieldStyles,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px !important",
                                overflow: "hidden",
                              },
                              "& .MuiInputAdornment-root": {
                                paddingLeft: "5px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </LocalizationProvider>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>
                    Expected Shipment Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      sx={{
                        ...styles.datePickerOuter,
                        borderRadius: "8px !important",
                      }}
                    >
                      <DatePicker
                        value={expectedShipmentDate}
                        onChange={(newValue) =>
                          handleFormChange("expectedShipmentDate", newValue)
                        }
                        minDate={salesOrderDate}
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            sx={{
                              ...styles.datePicker,
                              ...textFieldStyles,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px !important",
                                overflow: "hidden",
                              },
                              "& .MuiInputAdornment-root": {
                                paddingLeft: "5px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </LocalizationProvider>
                </Box>
              </Grid>
              {/* Second Row */}
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={styles.label}>Payment Terms</Typography>
                    <Select
                      value={paymentTerm}
                      onChange={(e) =>
                        handleFormChange("paymentTerm", e.target.value)
                      }
                      error={!!errors.paymentTerm}
                      helperText={errors.paymentTerm}
                      fullWidth
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: "#999" }}>
                            Select Payment Terms
                          </span>
                        )
                      }
                      sx={{ ...textFieldStyles }}
                    >
                      {paymentTermsOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={styles.label}>Delivery Method</Typography>
                    <Select
                      value={deliveryMethod}
                      sx={{ ...textFieldStyles }}
                      onChange={(e) =>
                        handleFormChange("deliveryMethod", e.target.value)
                      }
                      error={!!errors.deliveryMethod}
                      helperText={errors.deliveryMethod}
                      fullWidth
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: "#999" }}>
                            Select Delivery Method
                          </span>
                        )
                      }
                    >
                      <MenuItem value="Courier">Courier</MenuItem>
                      <MenuItem value="Pickup">Pickup</MenuItem>
                      <MenuItem value="Standard Shipping">
                        Standard Shipping
                      </MenuItem>
                      <MenuItem value="Express Shipping">
                        Express Shipping
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Third Row */}
              <Grid item xs={12} md={12}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Subject</Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter subject"
                    size="small"
                    onChange={(e) =>
                      handleFormChange("subject", e.target.value)
                    }
                    error={!!errors.subject}
                    helperText={errors.subject}
                    sx={{
                      ...styles.multilineTextField,
                      ...textFieldStyles,
                      "& .MuiOutlinedInput-root": {
                        height: "76px",
                        borderRadius: "8px !important",
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box sx={styles.customerNotes}>
                  <Typography>Customer Notes</Typography>
                  <TextField
                    rows={2}
                    fullWidth
                    placeholder="Enter customer notes"
                    onChange={(e) =>
                      handleFormChange("customerNotes", e.target.value)
                    }
                    error={!!errors.customerNotes}
                    helperText={errors.customerNotes}
                    sx={{
                      ...styles.multilineTextField,
                      marginTop: "8px !important",
                      "& .MuiOutlinedInput-root": {
                        height: "76px",
                        borderRadius: "8px",
                        "& fieldset": {
                          border: "1px solid #D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#28AA4A",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#28AA4A",
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {/* Items Section */}
        <Box>
          <Typography sx={{ ...styles.itemsTitle }}>
            Items & Services
          </Typography>
          <Box sx={styles.container}>
            <Box sx={styles.itemsSection}>
              {items.map((item, index) => {
                return (
                  <Box key={index} sx={styles.itemRow}>
                    <Autocomplete
                      id={`item-select-${index}`}
                      options={[
                        { _id: "add_new", title: "+ New Item" },
                        ...(itemList || []),
                      ]}
                      getOptionLabel={(option) => option?.title || ""}
                      value={
                        itemList?.find((i) => i.title === item.name) || null
                      }
                      onChange={(event, newValue) => {
                        if (newValue?._id === "add_new") {
                          navigate("/dashboard/addNewItem"); // redirect page
                          return;
                        }
                        if (!newValue) {
                          handleItemChange("", index, "name");
                          handleItemChange("", index, "invoiceQuantity");
                          return;
                        }
                        handleItemChange(newValue?.title || "", index, "name");
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={
                            option._id === "add_new"
                              ? { color: color.secondary }
                              : {}
                          }
                        >
                          {option.title}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Item Name"
                          size="small"
                          sx={{ ...styles.itemField, ...textFieldStyles }}
                          error={!!errors.items[index]?.item}
                          helperText={errors.items[index]?.item}
                        />
                      )}
                    />
                    <TextField
                      size="small"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) =>
                        handleItemChange(e.target.value, index, "description")
                      }
                      sx={{
                        ...styles.descriptionField,
                        ...textFieldStyles,
                        display: "flex",
                      }}
                      error={!!errors.items[index]?.description}
                      helperText={errors.items[index]?.description}
                      SelectProps={{
                        displayEmpty: true,
                        IconComponent: () => null,
                        renderValue: (selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "#999" }}>Account</span>
                            );
                          return selected === "64f27b8e1234567890abcdef"
                            ? "John Doe"
                            : "Jane Smith";
                        },
                      }}
                    ></TextField>
                    <Grid item xs={12} lg={2}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ ...styles.customInput, width: "100%" }}
                      >
                        <TextField
                          value={items[index]?.invoiceQuantity}
                          type="number"
                          placeholder="Quantity"
                          sx={{ ...styles.quality, ...textFieldStyles }}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            const maxQuantity = items[index]?.quantity;
                            if (
                              !isNaN(value) &&
                              value >= 0 &&
                              value <= maxQuantity
                            ) {
                              handleItemChange(value, index, "invoiceQuantity");
                            } else if (e.target.value === "") {
                              handleItemChange("", index, "invoiceQuantity");
                            } else {
                              const validValue = value < 0 ? 0 : maxQuantity;
                              handleItemChange(
                                validValue,
                                index,
                                "invoiceQuantity"
                              );
                            }
                          }}
                          error={!!errors.items[index]?.quantity}
                          helperText={errors.items[index]?.quantity}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" variant="filled">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#DEDEDE",
                                    height: 41,
                                    p: 0,
                                    ...styles.inputBgBox,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 35,
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "end",
                                      fontWeight: "600",
                                      userSelect: "none",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    {items[index]?.quantity}
                                  </Box>
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <Box
                                        sx={{
                                          bgcolor: "white",
                                          p: 1,
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            color: "#373D3F",
                                            mb: 1,
                                          }}
                                        >
                                          Stock Details
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            color: "#373D3F",
                                            fontWeight: 400,
                                          }}
                                        >
                                          Stock on Hand :{" "}
                                          {items[index]?.quantity || "0"}
                                        </Typography>
                                      </Box>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "white !important",
                                          p: 0,
                                          border:
                                            "1px solid #878787 !important",
                                          boxShadow: "none !important",
                                          "& .MuiTooltip-arrow": {
                                            color: "white !important",
                                            "&::before": {
                                              border: "1px solid #878787",
                                              backgroundColor:
                                                "white !important",
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      sx={{
                                        color: "#878787",
                                      }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Grid>

                    <TextField
                      size="small"
                      type="number"
                      placeholder="Rate"
                      value={
                        items[index]?.rate === ""
                          ? ""
                          : String(items[index]?.rate)
                      }
                      onChange={(e) =>
                        handleItemChange(e.target.value, index, "rate")
                      }
                      sx={{ ...styles.rateField, ...textFieldStyles }}
                      error={!!errors.items[index]?.rate}
                      helperText={errors.items[index]?.rate}
                    />
                    <TextField
                      size="small"
                      placeholder="Amount"
                      value={
                        (items[index]?.rate || 0) *
                        (items[index]?.invoiceQuantity || 0) || ""
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        width: "120px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          height: "41px",
                          "& fieldset": {
                            border: "1px solid #D9D9D9",
                          },
                        },
                      }}
                    />

                    {index === 0 ? (
                      <IconButton onClick={handleAddItem} sx={styles.addButton}>
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        sx={styles.deleteButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Summary Section */}
        <Box>
          <Typography sx={{ ...styles.itemsTitle, marginBottom: "20px" }}>
            Discount and Tax (%)
          </Typography>
          <Grid container spacing={0}>
            <Grid item xs={12} md={7}>
              <Box sx={styles.container4}>
                <Box
                  sx={{
                    height: "140px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Discount
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Discount"
                          value={discountPercentage || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 100) {
                              setDiscountPercentage(value);
                              handleFormChange("discount", value);
                            } else if (e.target.value === "") {
                              setDiscountPercentage(0);
                              handleFormChange("discount", 0);
                            }
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                          error={!!errors.discount}
                          helperText={errors.discount}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Tax
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Tax"
                          value={taxPercentage || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 100) {
                              setTaxPercentage(value);
                              handleFormChange("tax", value);
                            } else if (e.target.value === "") {
                              setTaxPercentage(0);
                              handleFormChange("tax", 0);
                            }
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                          error={!!errors.tax}
                          helperText={errors.tax}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={styles.container3}>
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "140px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>Total</Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      ${calculateSubTotal().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Tax ({taxPercentage || 0}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      ${calculateTaxValue().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Discount ({discountPercentage || 0}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      -${calculateDiscountValue().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 1,
                      borderTop: "1px solid #eee",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                      ${calculateGrandTotal().toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography
            sx={{
              ...styles.itemsTitle,
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            Terms & Conditions
          </Typography>
          <Grid container spacing={0}>
            <Grid item xs={12} md={7}>
              <Box sx={styles.container6}>
                <TextField
                  fullWidth
                  placeholder="Enter terms and conditions"
                  onChange={(e) =>
                    handleFormChange("termsAndConditions", e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      height: "76px",
                      borderRadius: "8px !important",
                    },
                  }}
                  error={!!errors.termsAndConditions}
                  helperText={errors.termsAndConditions}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={styles.container5}>
                <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                  Attach File to Sales Order
                </Typography>
                <Box
                  sx={{
                    ...styles.fileDropArea,
                    border: "2px dashed #ccc",
                    borderRadius: "8px !important",
                    p: 2,
                    minHeight: "76px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#28AA4A",
                    },
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <input
                    type="file"
                    id="quoteFileUpload"
                    style={{ display: "none" }}
                    onChange={handleFileChange} // Required for the linter
                  />
                  <label
                    htmlFor="quoteFileUpload"
                    style={{
                      cursor: "pointer",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {filePreview ? (
                      <Box sx={{ width: "100%", textAlign: "center" }}>
                        {selectedFile?.type.startsWith("image/") ? (
                          <img
                            src={filePreview}
                            alt="Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "60px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}
                          >
                            <Typography>{selectedFile?.name}</Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Button
                          variant="contained"
                          component="span" // This makes the button act as part of the label
                          startIcon={
                            <Box
                              component="span"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <img
                                src={humbbleIcon}
                                alt="Upload"
                                style={{ width: "16px", height: "16px" }}
                              />
                            </Box>
                          }
                          sx={{
                            backgroundColor: "#28AA4A",
                            color: "white",
                            textTransform: "none",
                            height: "36px",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "#229A41",
                            },
                          }}
                        >
                          Choose File
                        </Button>
                        <Typography sx={{ color: "#666" }}>
                          or drag and drop file here
                        </Typography>
                      </Box>
                    )}
                  </label>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ ...styles.container }}>
          <Box sx={styles.actionButtons}>
            <Button
              type="submit"
              variant="contained"
              sx={styles.draftButton}
              disabled={
                !customerId ||
                !salesOrderNumber ||
                !salesOrderDate ||
                !expectedShipmentDate ||
                !paymentTerm ||
                !deliveryMethod ||
                !subject ||
                !customerNotes ||
                !isItemValid
              }
            >
              Save as Draft
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={styles.saveButton}
              disabled={
                !customerId ||
                !salesOrderNumber ||
                !salesOrderDate ||
                !expectedShipmentDate ||
                !paymentTerm ||
                !deliveryMethod ||
                !subject ||
                !customerNotes ||
                !isItemValid
              }
            >
              Save
            </Button>
            <Button variant="outlined" sx={styles.cancelButton}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default CreateSalesOrderPage;
